
import { defineComponent } from "vue";
import { usePortalStore } from "@/framework/portal";
import CircularProgress from "@/plugins/circular-progress/circular-progress.vue";
import _ from "lodash";
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy } from "pdfjs-dist";

export default defineComponent({
    name: "InductionPage",
    inheritAttrs: false,
    components: {
        CircularProgress
    },
    props: {
        title: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: false
        },
        content: {
            type: String,
            required: true
        },
        pageName: {
            type: String,
            required: true
        },
        agreeBtnText: {
            type: String,
            required: true
        },
        disagreeBtnText: {
            type: String,
            required: true
        },
        pdfInstructionText: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loadedPdf: null as Promise<PDFDocumentProxy> | null,
            isPdf: false,
            isPdfScrolled: false,
            numPages: 1,
            disableOptions: false,
            inductionContent: "",
            isCompleted: null
        }
    },
    setup() {
        const portal = usePortalStore()
        return {
            portal
        };
    },

    created() {
        this.ResetPage();
    },

    watch: {
        isCompleted(val) {
            _.forEach(this.portal.inductions, (ind) => {
                if (ind.pageName === this.pageName) {
                    ind.isCompleted = val;
                }
            })
        },
        pageName(val) {
            this.ResetPage();
        }
    },

    methods: {
        ResetPage() {
            this.portal.currentPageName = this.pageName;
            const thisInduction = this.portal.getInduction(this.pageName);
            this.isCompleted = thisInduction.isCompleted;

            const languageValue = this.portal._translate(thisInduction.inductionText);

            // Clear any existing pdf content
            const hostElement = document.getElementById('pdfPages');
            if (hostElement)
                hostElement.innerHTML = '';

            if (!languageValue.startsWith("data:application/pdf;base64,")) {
                // Set Text
                this.inductionContent = languageValue;
                // Clear PDF
                this.isPdf = false;
                this.loadedPdf = null;
            }
            else {
                // Clear Text
                this.inductionContent = "";
                // Set PDF
                this.isPdf = true;
                this.loadedPdf = this.loadPdf(languageValue);
            }
        },
        ConvertBase64ToBinary(dataURI: string) {
            const BASE64_MARKER = ';base64,';
            const base64 = dataURI.substring(dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length);
            const raw = window.atob(base64);
            const rawLength = raw.length;
            const array = new Uint8Array(new ArrayBuffer(rawLength));

            for(let i = 0; i < rawLength; i++) {
                array[i] = raw.charCodeAt(i);
            }
            return array;
        },
        loadPdf(content: string) {
            GlobalWorkerOptions.workerSrc = new URL(
                '../../../node_modules/pdfjs-dist/legacy/build/pdf.worker.min.mjs',
                import.meta.url,
            ).toString();

            const pdfContent = this.ConvertBase64ToBinary(content);
            return getDocument(pdfContent).promise.then(x => {
                this.numPages = x.numPages;
                for (let i = 1; i <= this.numPages; i++)
                    this.renderPdfPage(i);
                this.isPdfScrolled = this.numPages <= 1; // Need to reset after pages are rendered
                this.disableOptions = !this.isPdfScrolled && this.isCompleted === null;
                return x;
            });
        },
        async renderPdfPage(num: number) {
            (await this.loadedPdf!).getPage(num).then(function (page) {
                const hostElement = document.getElementById('pdfPages')!;

                const canvas = hostElement.appendChild(document.createElement("canvas"));
                canvas.id = 'pdfPage' + num;
                canvas.style.width = "100%"
                hostElement.appendChild(document.createElement("br"));
                
                const context = canvas.getContext('2d');
                if (!context)
                    return;

                const viewport = page.getViewport({ scale: 1 });
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport
                };
                page.render(renderContext).promise;
            });
        },
        isPdfRead() {
            // If not a pdf OR is already scrolled OR is already completed - set as enabled
            if (!this.isPdf || this.isPdfScrolled || this.isCompleted !== null) {
                this.disableOptions = false;
                return;
            }

            const hostElement = document.getElementById('pdfPages');
            if (hostElement) {
                const threshold = (hostElement.scrollHeight - hostElement.offsetHeight) * 0.95; // bottom 5%
                if (hostElement.scrollTop > threshold) {
                    this.isPdfScrolled = true; // Mark as already scrolled
                    this.disableOptions = false;
                }
                else {
                    this.disableOptions = true;
                }
            }
            else {
                this.disableOptions = false;
            }
        }
    }    
});